import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";

/**
 * Dialogo de edicion de un reporte guardado (nombre, descripcion, icono,
 * visibilidad y, para admin, propietario). Vive fuera de los componentes
 * porque lo abren tanto el listado de reportes como los tabs del listado
 * dinamico.
 */

export const REPORT_ICONS = [
  "file-alt", "file", "folder", "folder-open", "chart-bar", "chart-line", 
  "chart-pie", "chart-area", "table", "list", "list-alt", "list-ul", "list-ol",
  "th", "th-list", "th-large", "clipboard", "clipboard-list", "clipboard-check",
  "tasks", "check-square", "calendar", "calendar-alt", "calendar-check",
  "clock", "history", "archive", "box", "boxes", "cube", "cubes",
  "database", "server", "hdd", "filter", "search", "eye", "eye-slash",
  "star", "star-half-alt", "heart", "bookmark", "flag", "tag", "tags",
  "user", "users", "user-friends", "user-tie", "user-cog", "user-check",
  "building", "home", "warehouse", "industry", "store", "store-alt",
  "dollar-sign", "euro-sign", "money-bill", "money-bill-alt", "coins", "wallet",
  "credit-card", "receipt", "file-invoice", "file-invoice-dollar",
  "shopping-cart", "cart-plus", "shopping-bag", "shopping-basket",
  "truck", "shipping-fast", "box-open", "dolly", "pallet",
  "globe", "globe-americas", "map", "map-marker", "map-marker-alt", "location-arrow",
  "plane", "ship", "anchor", "car", "bus", "train",
  "envelope", "envelope-open", "paper-plane", "inbox", "comment", "comments",
  "phone", "phone-alt", "mobile-alt", "fax", "headset",
  "cog", "cogs", "wrench", "tools", "sliders-h", "toolbox",
  "bolt", "fire", "sun", "moon", "cloud", "umbrella",
  "bell", "bullhorn", "megaphone", "exclamation", "exclamation-circle", "info-circle",
  "question-circle", "check-circle", "times-circle", "plus-circle", "minus-circle",
  "lock", "unlock", "key", "shield-alt", "fingerprint",
  "pen", "pencil-alt", "edit", "eraser", "highlighter",
  "print", "file-pdf", "file-excel", "file-word", "file-image",
  "download", "upload", "cloud-download-alt", "cloud-upload-alt",
  "sync", "redo", "undo", "exchange-alt", "random",
  "sort", "sort-alpha-down", "sort-amount-down", "sort-numeric-down",
  "percentage", "calculator", "balance-scale", "weight",
  "barcode", "qrcode", "hashtag", "at", "link",
  "expand", "compress", "arrows-alt", "external-link-alt",
  "share", "share-alt", "reply", "forward",
  "play", "pause", "stop", "step-forward", "step-backward",
  "volume-up", "volume-down", "volume-mute", "music",
  "image", "images", "camera", "video", "film",
  "magic", "wand-magic", "lightbulb", "brain", "robot",
  "graduation-cap", "book", "book-open", "bookmark", "newspaper"
];

export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Usuarios asignables como propietario del reporte (solo admin). */
export async function fetchAssignableUsers() {
  try {
    const { data } = await wencesApi.get("/api/users/all");
    if (!data?.ok || !Array.isArray(data.data)) return [];
    return data.data
      .filter(
        (user) =>
          user &&
          user._id &&
          (user.name || user.email) &&
          user.status !== "deactivated" &&
          !user.deleted
      )
      .map((user) => ({
        _id: String(user._id),
        name: user.name || user.email,
        email: user.email || "",
      }))
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
  } catch (error) {
    console.error("Error loading system users:", error);
    return [];
  }
}

function buildCreatedBySelectHtml(report, systemUsers, t) {
  const currentCreatedById = String(
    report.createdBy?._id || report.createdBy || ""
  );
  const options = systemUsers
    .map((user) => {
      const selected = user._id === currentCreatedById ? " selected" : "";
      const label = escapeHtml(user.name || user.email);
      return `<option value="${escapeHtml(user._id)}"${selected}>${label}</option>`;
    })
    .join("");

  return `
    <div class="mt-3 text-start">
      <label class="form-label mb-1" for="swal-created-by">${t("CreatedBy")}</label>
      <select id="swal-created-by" class="form-select form-select-sm">
        ${options}
      </select>
    </div>
  `;
}

/**
 * Abre el popup de edicion y, si se confirma, guarda los cambios.
 *
 * @returns {Promise<object|null>} el reporte actualizado, o null si se cancelo
 *   o fallo el guardado.
 */
export async function openEditReportDialog({
  report,
  t,
  isAdmin = false,
  systemUsers = null,
}) {
  if (!report || !report._id) return null;

  const users =
    isAdmin && !systemUsers ? await fetchAssignableUsers() : systemUsers || [];

  const currentIcon = report.icon || "file-alt";
  const iconGridHtml = REPORT_ICONS.map(
    (icon) =>
      `<button type="button" class="icon-btn${
        icon === currentIcon ? " selected" : ""
      }" data-icon="${icon}" title="${icon}">
        <i class="fas fa-${icon}"></i>
      </button>`
  ).join("");
  const createdBySelectHtml =
    isAdmin && users.length > 0
      ? buildCreatedBySelectHtml(report, users, t)
      : "";

  const { value: formValues } = await Swal.fire({
    title: t("EditReport"),
    html: `
      <input id="swal-name" class="swal2-input" placeholder="${t("ReportName")}" value="${escapeHtml(report.name)}">
      <textarea id="swal-description" class="swal2-textarea" placeholder="${t("Description")}">${escapeHtml(report.description || "")}</textarea>
      <div class="icon-selector-container mt-3">
        <label class="form-label text-start d-block mb-2">${t("ReportIcon")}</label>
        <div class="selected-icon-display mb-2">
          <i id="preview-icon" class="fas fa-${currentIcon} fa-lg"></i>
          <span id="preview-icon-name" class="ms-2 text-muted">${currentIcon}</span>
        </div>
        <input type="hidden" id="swal-icon" value="${currentIcon}">
        <input type="text" id="icon-search" class="form-control form-control-sm mb-2" placeholder="${t("SearchIcon")}">
        <div class="icon-grid-edit">
          ${iconGridHtml}
        </div>
      </div>
      <div class="form-check mt-3">
        <input class="form-check-input" type="checkbox" id="swal-public" ${report.isPublic ? "checked" : ""}>
        <label class="form-check-label" for="swal-public">
          ${t("MakeReportPublic")}
        </label>
      </div>
      ${createdBySelectHtml}
    `,
    width: 600,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: t("Save"),
    cancelButtonText: t("Cancel"),
    didOpen: () => {
      const iconBtns = document.querySelectorAll(".icon-btn");
      iconBtns.forEach((btn) => {
        btn.addEventListener("click", () => {
          iconBtns.forEach((b) => b.classList.remove("selected"));
          btn.classList.add("selected");
          const icon = btn.dataset.icon;
          document.getElementById("swal-icon").value = icon;
          document.getElementById("preview-icon").className = `fas fa-${icon} fa-lg`;
          document.getElementById("preview-icon-name").textContent = icon;
        });
      });

      const searchInput = document.getElementById("icon-search");
      searchInput.addEventListener("input", (e) => {
        const search = e.target.value.toLowerCase();
        iconBtns.forEach((btn) => {
          const iconName = btn.dataset.icon;
          btn.style.display = iconName.includes(search) ? "" : "none";
        });
      });
    },
    preConfirm: () => {
      const payload = {
        name: document.getElementById("swal-name").value,
        description: document.getElementById("swal-description").value,
        isPublic: document.getElementById("swal-public").checked,
        icon: document.getElementById("swal-icon").value,
      };
      if (isAdmin) {
        const createdByEl = document.getElementById("swal-created-by");
        if (createdByEl?.value) {
          payload.createdBy = createdByEl.value;
        }
      }
      return payload;
    },
  });

  if (!formValues) return null;

  try {
    const { data } = await wencesApi.put(
      `/api/gotrading/reports/${report._id}`,
      formValues
    );
    Swal.fire({
      icon: "success",
      title: t("success"),
      text: t("ReportUpdatedSuccessfully"),
      timer: 2000,
      showConfirmButton: false,
    });
    return data && data._id ? data : { ...report, ...formValues };
  } catch (error) {
    console.error("Error updating report:", error);
    Swal.fire({
      icon: "error",
      title: t("error"),
      text: error.response?.data?.error || t("ErrorUpdatingReport"),
    });
    return null;
  }
}

/** El dueño del reporte (o un admin) puede editarlo. */
export function canEditReport(report, currentUserId, isAdmin) {
  if (isAdmin) return true;
  if (!currentUserId || !report || !report.createdBy) return false;
  return (
    String(report.createdBy._id || report.createdBy) === String(currentUserId)
  );
}
